import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EndeavorApi } from '../services/ApiService';
import { EndeavorMetadataApi } from '../services/api/EndeavorMetadataApi';
import { useData } from '../contexts/DataContext';
import CourseScheduleEditor from './CourseScheduleEditor';
import './CourseView.css';
import './CourseEdit.css';

const LoadingState = ({ message = 'Loading course...' }) => (
  <div className="course-view-container animate-fade-in-up">
    <div className="loading-state">
      <div className="loading-spinner" />
      <p>{message}</p>
    </div>
  </div>
);

const ErrorState = ({ error, onGoBack }) => (
  <div className="course-view-container animate-fade-in-up">
    <div className="error-state">
      <div className="error-icon">⚠️</div>
      <h2>Error</h2>
      <p>{error}</p>
      <button className="btn btn-primary" onClick={onGoBack}>Go Back</button>
    </div>
  </div>
);

const getMetadataValue = (course, name) => {
  if (!course?.metadata) return null;
  const entry = course.metadata.find(m => (m.metadataName || '').toLowerCase() === (name || '').toLowerCase());
  return entry?.value ?? null;
};

export default function CourseEdit() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { updateEndeavor, clearCache, getEndeavor, removeEndeavor } = useData();

  const [state, setState] = useState({ course: null, loading: true, saving: false, deleting: false, error: null });
  const [formData, setFormData] = useState({ name: '', description: '', startDate: '', endDate: '', courseCode: '', professor: '', officeHours: '' });
  const [metadataItems, setMetadataItems] = useState([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareConfirmOpen, setShareConfirmOpen] = useState(false);

  const updateState = useCallback((u) => setState(prev => ({ ...prev, ...u })), []);

  const toInputDate = useCallback((d) => {
    if (!d) return '';
    try {
      const s = String(d).trim();
      // If server returned a date-only string (YYYY-MM-DD), use it directly
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

      // If ISO contains timezone (Z or offset), parse as UTC and extract UTC date
      const hasTZ = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
      const dt = new Date(s);
      if (isNaN(dt.getTime())) return '';
      if (hasTZ) {
        const y = dt.getUTCFullYear();
        const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dt.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }

      // Timezone-less ISO (e.g. '2025-09-25T00:00:00') is interpreted as local by JS.
      // In that case, use local date components so the user's selected calendar day is preserved.
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch {
      return '';
    }
  }, []);

  const fetchCourseData = useCallback(async () => {
    if (!courseId) return;
    try {
      updateState({ loading: true, error: null });
      const r = await EndeavorApi.getEndeavor(courseId);
      if (!r.ok) throw new Error(`fetch failed ${r.status}`);
      const raw = await r.json();

      const normalizedStart = raw.startDate || getMetadataValue(raw, 'Start Date');
      const normalizedEnd = raw.endDate || getMetadataValue(raw, 'End Date');
      const metaMap = (raw.metadata || []).reduce((acc, m) => { if (m.metadataName) acc[m.metadataName.toLowerCase()] = m.value; return acc; }, {});

      setFormData({
        name: raw.name || '',
        description: raw.description || metaMap['description'] || '',
        startDate: toInputDate(normalizedStart),
        endDate: toInputDate(normalizedEnd),
        courseCode: metaMap['course code'] || metaMap['coursecode'] || '',
        officeHours: metaMap['office hours'] || '',
        professor: metaMap['professor'] || ''
      });

      setMetadataItems(Array.isArray(raw.metadata) ? raw.metadata : []);
      updateState({ course: raw, loading: false });
    } catch (e) {
      console.error('fetchCourseData error', e);
      updateState({ error: e.message || 'Failed to load', loading: false });
    }
  }, [courseId, updateState, toInputDate]);

  useEffect(() => { fetchCourseData(); }, [fetchCourseData]);

  const handleInputChange = useCallback((e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); }, []);

  const formatDate = useCallback((dateString) => { if (!dateString) return 'Not set'; try { let date = dateString.includes('T') ? new Date(dateString) : new Date(`${dateString}T00:00:00`); if (isNaN(date.getTime())) return 'Invalid date'; return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return 'Invalid date'; } }, []);

  const courseInfo = useMemo(() => { if (!state.course) return null; return { createdAt: formatDate(state.course.createdAt), updatedAt: formatDate(state.course.updatedAt), typeName: state.course.endeavorTypeName || state.course.endeavorType?.name || 'Course' }; }, [state.course, formatDate]);

  const handleSave = useCallback(async () => {
    if (!state.course) return;
    try {
      updateState({ saving: true, error: null });

      // Send date-only strings (YYYY-MM-DD) to avoid timezone conversions on the server
      const payload = {
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate ? String(formData.startDate) : null,
        endDate: formData.endDate ? String(formData.endDate) : null,
        endeavorTypeId: state.course.endeavorTypeId || state.course.endeavorType?.id
      };

  console.log('[CourseEdit] Saving endeavor payload:', payload);
  const res = await updateEndeavor(courseId, payload);
  console.log('[CourseEdit] updateEndeavor response:', res && { ok: res.ok, status: res.status });
      if (!res || !res.ok) { const txt = res && res.text ? await res.text() : 'no-response'; throw new Error(txt); }

  // keep date-only values for metadata updates as well
  const normalizedStartISO = formData.startDate ? String(formData.startDate) : null;
  const normalizedEndISO = formData.endDate ? String(formData.endDate) : null;

      const adjustedMetadataItems = metadataItems.map(m => {
        const name = (m.metadataName || '').toLowerCase();
        if (name === 'description') return { ...m, value: formData.description };
        if (name === 'start date') return { ...m, value: normalizedStartISO };
        if (name === 'end date') return { ...m, value: normalizedEndISO };
        if (name === 'course code') return { ...m, value: formData.courseCode };
        if (name === 'professor') return { ...m, value: formData.professor };
        if (name === 'office hours') return { ...m, value: formData.officeHours };
        return m;
      });

      const originalById = new Map((state.course.metadata || []).map(m => [m.id, m]));
      const changes = adjustedMetadataItems.filter(m => { const orig = originalById.get(m.id); return orig && String(orig.value ?? '') !== String(m.value ?? ''); });

      if (changes.length > 0) {
        const results = await Promise.all(changes.map(async (item) => {
          // Server expects update payload with fieldName/fieldValue (see API docs)
          const body = {
            id: item.id,
            endeavorId: item.endeavorId || Number(courseId),
            fieldName: item.metadataName || item.metadataDefinitionId || undefined,
            fieldValue: item.value
          };
          console.log('[CourseEdit] Updating metadata id=', item.id, 'body=', body);
          const r = await EndeavorMetadataApi.updateMetadata(item.id, body);
          console.log('[CourseEdit] metadata update response for id=', item.id, r && { ok: r.ok, status: r.status });
          return { ok: r && r.ok, status: r ? r.status : 'no-response', resp: r };
        }));

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) {
          const msgs = await Promise.all(failed.map(async f => { try { return f.resp && f.resp.text ? await f.resp.text() : `${f.status}` } catch { return `${f.status}` } }));
          throw new Error(`Metadata update failed: ${msgs.join(' | ')}`);
        }

        try {
          clearCache(`endeavor_${courseId}`);
          clearCache('endeavors');
          const warmed = await getEndeavor(courseId);
          console.log('[CourseEdit] warmed endeavor after save:', warmed);
          setMetadataItems(adjustedMetadataItems);
          if (warmed) {
            updateState({ course: warmed });
            const newStart = toInputDate(warmed.startDate || getMetadataValue(warmed, 'Start Date'));
            const newEnd = toInputDate(warmed.endDate || getMetadataValue(warmed, 'End Date'));
            console.log('[CourseEdit] normalized warmed dates', { newStart, newEnd });
            setFormData(prev => ({
              ...prev,
              startDate: newStart,
              endDate: newEnd
            }));
          }
        } catch (e) { console.warn('cache refresh failed', e); }
      }

      navigate(`/course/${courseId}`);
    } catch (e) {
      updateState({ error: `Failed to save: ${e.message}`, saving: false });
    }
  }, [courseId, formData, metadataItems, state.course, updateEndeavor, clearCache, getEndeavor, navigate, updateState, toInputDate]);

  const handleDeleteCourse = useCallback(async () => {
    try {
      updateState({ deleting: true, error: null });
      await removeEndeavor(courseId);
      navigate('/dashboard');
    } catch (e) { updateState({ error: `Failed to delete: ${e.message}`, deleting: false }); setShowDeleteConfirmation(false); }
  }, [courseId, navigate, updateState, removeEndeavor]);

  const handleShareCourse = useCallback(async () => {
    try {
      setSharing(true);
      updateState({ error: null });
      const resp = await EndeavorApi.shareEndeavor(courseId);
      if (!resp || !resp.ok) {
        const txt = resp && resp.text ? await resp.text() : `status ${resp && resp.status}`;
        throw new Error(txt);
      }
      const updated = await resp.json().catch(() => null);
      try {
        clearCache(`endeavor_${courseId}`);
        clearCache('endeavors');
      } catch (_) { /* ignore */ }
      if (updated) {
        updateState({ course: { ...(state.course || {}), ...updated } });
      }
      setShareConfirmOpen(false);
      // Owner can now open the Manage tab to create invite codes.
      navigate(`/course/${courseId}`);
    } catch (e) {
      updateState({ error: `Failed to share course: ${e.message}` });
    } finally {
      setSharing(false);
    }
  }, [courseId, clearCache, navigate, state.course, updateState]);

  const { course, loading, saving, deleting, error } = state;
  const isShared = !!course && (course.ownershipType === 'student_shared' || course.ownershipType === 'org_owned');
  const canShare = !!course
    && (!course.ownershipType || course.ownershipType === 'personal')
    && !isShared;

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onGoBack={() => navigate(`/course/${courseId}`)} />;
  if (!course) return null;

  return (
    <div className="course-view-container animate-fade-in-up">
      {showDeleteConfirmation && (
        <div className="delete-confirmation-overlay" role="dialog" aria-modal="true">
          <div className="delete-confirmation-modal">
            <div className="delete-confirmation-header">
              <h3>Delete Course</h3>
              <button className="close-button" onClick={() => setShowDeleteConfirmation(false)} disabled={deleting} aria-label="Close">✕</button>
            </div>
            <div className="delete-confirmation-body">
              <div className="warning-icon">⚠️</div>
              <p className="delete-lead">Are you sure you want to delete the course <strong>"{formData.name || course.name}"</strong>?</p>
              <p>This action cannot be undone. All associated data will be permanently removed.</p>
            </div>
            <div className="delete-confirmation-actions">
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirmation(false)} disabled={deleting}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteCourse} disabled={deleting}>{deleting ? (<><div className="loading-spinner small"/>Deleting...</>) : 'Delete Course'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="edit-mode-banner"><div className="edit-mode-content"><span className="edit-mode-icon">✏️</span><span className="edit-mode-text">Edit Mode - Update course details below</span></div></div>

      <button onClick={() => navigate(`/course/${courseId}`)} className="back-btn animate-slide-in-left">← Back to Course</button>

      <div className="course-header">
        <div className="course-title-section">
          <h1 className="course-title">{course.name}</h1>
          <div className="edit-mode-subtitle">Type: {courseInfo?.typeName} • Last Updated: {courseInfo?.updatedAt}</div>
        </div>
        {/* header-actions intentionally removed — actions live in sticky footer only */}
      </div>

      <div className="course-content">
        <div className="course-section">
          <h2><span className="section-icon">✏️</span> Edit Course</h2>
          <div className="edit-grid">
            <div className="left-col">
              <div className="course-edit-form">
                <div className="form-section"><h4>Course Info</h4></div>
                <div className="form-group full-width">
                  <label htmlFor="name">Course Name</label>
                  <input id="name" name="name" value={formData.name} onChange={handleInputChange} className="form-input" placeholder="Enter course name" required disabled={saving || deleting} />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="courseCode">Course Code</label>
                  <input id="courseCode" name="courseCode" value={formData.courseCode || ''} onChange={handleInputChange} className="form-input" placeholder="Course code" disabled={saving || deleting} />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="description">Description</label>
                  <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} className="form-textarea description" placeholder="Describe the course..." disabled={saving || deleting} />
                </div>

                {/* Schedule (moved into left column) */}
                <div className="form-row date-grid">
                  <div className="form-group">
                    <label htmlFor="startDate">Start Date</label>
                    <div className="input-with-icon input-date"><input type="date" id="startDate" name="startDate" value={formData.startDate} onChange={handleInputChange} className="form-input" disabled={saving || deleting} /></div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="endDate">End Date</label>
                    <div className="input-with-icon input-date"><input type="date" id="endDate" name="endDate" value={formData.endDate} onChange={handleInputChange} className="form-input" disabled={saving || deleting} /></div>
                  </div>
                </div>

                {/* Instructor & Office Hours (two-column) */}
                <div className="form-row two-cols">
                  <div className="form-group">
                    <label htmlFor="professor">Professor</label>
                    <input type="text" id="professor" name="professor" value={formData.professor || ''} onChange={handleInputChange} className="form-input standard" placeholder="Professor name" disabled={saving || deleting} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="officeHours">Office Hours</label>
                    <input type="text" id="officeHours" name="officeHours" value={formData.officeHours || ''} onChange={handleInputChange} className="form-input standard" placeholder="Office hours (e.g. Tue 2-4pm)" disabled={saving || deleting} />
                  </div>
                </div>

                {/* Additional metadata removed per request */}
              </div>
            </div>
          </div>
        </div>

        <CourseScheduleEditor
          courseId={courseId}
          startDate={formData.startDate}
          endDate={formData.endDate}
        />

        <div className="sticky-footer">
          <div className="sticky-inner">
            <div className="left-actions">
              <button className="btn btn-danger" onClick={() => setShowDeleteConfirmation(true)} disabled={saving || deleting || sharing}>🗑️ Delete Course</button>
              {canShare && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setShareConfirmOpen(true)}
                  disabled={saving || deleting || sharing}
                  title="Make this course shareable so other students can join with an invite code"
                >
                  {sharing ? (<><div className="loading-spinner small"/>Sharing...</>) : '🔗 Share this course'}
                </button>
              )}
              {isShared && (
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/course/${courseId}/admin`)}
                  disabled={saving || deleting || sharing}
                  title="Manage this shared course (members, invite codes, settings)"
                >
                  ⚙️ Manage course
                </button>
              )}
            </div>
            <div className="right-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || deleting || sharing}>{saving ? (<><div className="loading-spinner small"/>Saving...</>) : '💾 Save All Changes'}</button>
              <button className="btn btn-secondary" onClick={() => navigate(`/course/${courseId}`)} disabled={saving || deleting || sharing}>Cancel & Return</button>
            </div>
          </div>
        </div>
      </div>

      {shareConfirmOpen && (
        <div className="delete-confirmation-overlay" role="dialog" aria-modal="true">
          <div className="delete-confirmation-modal">
            <div className="delete-confirmation-header">
              <h3>Share this course?</h3>
              <button className="close-button" onClick={() => setShareConfirmOpen(false)} disabled={sharing} aria-label="Close">✕</button>
            </div>
            <div className="delete-confirmation-body">
              <p className="delete-lead">Convert <strong>"{formData.name || course.name}"</strong> into a shared course.</p>
              <p>You'll be able to generate invite codes so other students can join. You will remain the owner.</p>
            </div>
            <div className="delete-confirmation-actions">
              <button className="btn btn-secondary" onClick={() => setShareConfirmOpen(false)} disabled={sharing}>Cancel</button>
              <button className="btn btn-primary" onClick={handleShareCourse} disabled={sharing}>
                {sharing ? (<><div className="loading-spinner small"/>Sharing...</>) : 'Yes, share course'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
